import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtTL, todayIso } from "@/lib/format";

const STATUS_OPTIONS = [
  { value: "pending", label: "Beklemede" },
  { value: "shipped", label: "Gönderildi" },
  { value: "returned", label: "İade" },
  { value: "cancelled", label: "İptal" },
];

const empty = () => ({
  order_no: "",
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  customer_address: "",
  order_date: todayIso(),
  shipping_date: "",
  product_code: "",
  product_name: "",
  quantity: 1,
  purchase_price: 0,
  sale_price: 0,
  shipping_fee: 0,
  commission_percent: 0,
  shipping_company: "",
  service_fee: 0,
  marketplace_id: "",
  status: "pending",
  notes: "",
});

const Section = ({ title, children }) => (
  <div className="azd-card p-6">
    <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">{title}</div>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[#111827] mb-1.5">
      {label} {required && <span className="text-[#DC2626]">*</span>}
    </label>
    {children}
  </div>
);

export default function EcommerceSaleForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ecommerce/marketplaces").then((r) => setMarkets(r.data.filter(m => m.is_active)));
    if (isEdit) {
      api.get(`/ecommerce/sales/${id}`).then((r) => setForm({ ...empty(), ...r.data }))
        .catch(() => { toast.error("Satış bulunamadı"); navigate("/ecommerce/sales"); });
    }
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live totals
  const qty = Number(form.quantity) || 0;
  const salep = Number(form.sale_price) || 0;
  const purchp = Number(form.purchase_price) || 0;
  const ship = Number(form.shipping_fee) || 0;
  const svc = Number(form.service_fee) || 0;
  const cpct = Number(form.commission_percent) || 0;
  const gross = qty * salep;
  const commAmt = gross * cpct / 100;
  const netT = gross - commAmt - ship - svc;
  const cost = qty * purchp;
  const profit = netT - cost;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Müşteri adı gerekli");
    if (!form.marketplace_id) return toast.error("Pazaryeri seçin");
    if (!form.product_code.trim() || !form.product_name.trim())
      return toast.error("Ürün kodu ve adı gerekli");
    if (qty <= 0) return toast.error("Adet 0'dan büyük olmalı");

    setSaving(true);
    try {
      const payload = {
        ...form,
        order_no: form.order_no.trim() || null,
        quantity: qty,
        purchase_price: purchp,
        sale_price: salep,
        shipping_fee: ship,
        commission_percent: cpct,
        service_fee: svc,
        shipping_date: form.shipping_date || null,
      };
      if (isEdit) {
        await api.put(`/ecommerce/sales/${id}`, payload);
        toast.success("Satış güncellendi");
      } else {
        await api.post("/ecommerce/sales", payload);
        toast.success("E-ticaret satışı eklendi");
      }
      navigate("/ecommerce/sales");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 max-w-6xl">
      <Section title="Sipariş & Müşteri Bilgileri">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Sipariş No (boş bırakılırsa otomatik)">
            <Input value={form.order_no} onChange={(e) => set("order_no", e.target.value)}
                   placeholder="EC-2026-00001" data-testid="ec-order-no" />
          </Field>
          <Field label="Sipariş Tarihi" required>
            <Input type="date" value={form.order_date}
                   onChange={(e) => set("order_date", e.target.value)} required
                   data-testid="ec-order-date" />
          </Field>
          <Field label="Gönderim Tarihi">
            <Input type="date" value={form.shipping_date || ""}
                   onChange={(e) => set("shipping_date", e.target.value)}
                   data-testid="ec-ship-date" />
          </Field>
          <Field label="Müşteri Ad Soyad" required>
            <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)}
                   required data-testid="ec-cust-name" />
          </Field>
          <Field label="Müşteri Telefon">
            <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)}
                   placeholder="+90 5XX XXX XX XX" data-testid="ec-cust-phone" />
          </Field>
          <Field label="Müşteri E-posta">
            <Input type="email" value={form.customer_email}
                   onChange={(e) => set("customer_email", e.target.value)}
                   data-testid="ec-cust-email" />
          </Field>
          <div className="md:col-span-3">
            <Field label="Teslimat Adresi">
              <Textarea value={form.customer_address}
                        onChange={(e) => set("customer_address", e.target.value)}
                        rows={2} data-testid="ec-cust-address" />
            </Field>
          </div>
        </div>
      </Section>

      <Section title="Ürün Bilgileri">
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Ürün Kodu" required>
            <Input value={form.product_code} onChange={(e) => set("product_code", e.target.value)}
                   required data-testid="ec-product-code" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Ürün Adı" required>
              <Input value={form.product_name} onChange={(e) => set("product_name", e.target.value)}
                     required data-testid="ec-product-name" />
            </Field>
          </div>
          <Field label="Adet" required>
            <Input type="number" min="0.01" step="0.01" value={form.quantity}
                   onChange={(e) => set("quantity", e.target.value)} required
                   data-testid="ec-qty" />
          </Field>
        </div>
      </Section>

      <Section title="Fiyat & Komisyon">
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Alış Fiyatı (Birim, TL)" required>
            <Input type="number" min="0" step="0.01" value={form.purchase_price}
                   onChange={(e) => set("purchase_price", e.target.value)} required
                   data-testid="ec-purchase" />
          </Field>
          <Field label="Satış Fiyatı (Birim, TL)" required>
            <Input type="number" min="0" step="0.01" value={form.sale_price}
                   onChange={(e) => set("sale_price", e.target.value)} required
                   data-testid="ec-sale" />
          </Field>
          <Field label="Kargo Ücreti (TL)">
            <Input type="number" min="0" step="0.01" value={form.shipping_fee}
                   onChange={(e) => set("shipping_fee", e.target.value)}
                   data-testid="ec-shipping-fee" />
          </Field>
          <Field label="Komisyon (%)">
            <Input type="number" min="0" step="0.01" max="100" value={form.commission_percent}
                   onChange={(e) => set("commission_percent", e.target.value)}
                   data-testid="ec-commission" />
          </Field>
          <Field label="Kargo Firması">
            <Input value={form.shipping_company}
                   onChange={(e) => set("shipping_company", e.target.value)}
                   placeholder="Yurtiçi, MNG, Aras..." data-testid="ec-shipping-co" />
          </Field>
          <Field label="Hizmet Bedeli (TL)">
            <Input type="number" min="0" step="0.01" value={form.service_fee}
                   onChange={(e) => set("service_fee", e.target.value)}
                   data-testid="ec-service-fee" />
          </Field>
          <Field label="Pazaryeri" required>
            <Select value={form.marketplace_id} onValueChange={(v) => set("marketplace_id", v)}>
              <SelectTrigger data-testid="ec-marketplace"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {markets.length === 0 && (
                  <SelectItem value="__empty__" disabled>Önce pazaryeri tanımlayın</SelectItem>
                )}
                {markets.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sipariş Durumu">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="ec-status-sel"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Live totals card */}
        <div className="mt-5 grid md:grid-cols-5 gap-3 bg-[#F6F8FB] border border-[#E5E7EB] rounded-lg p-4">
          <Kpi label="Brüt Ciro" value={fmtTL(gross)} color="text-[#0B3676]" />
          <Kpi label="Komisyon" value={fmtTL(commAmt)} color="text-[#DC2626]" />
          <Kpi label="Net Ciro" value={fmtTL(netT)} color="text-[#08285A]" />
          <Kpi label="Maliyet" value={fmtTL(cost)} color="text-[#6B7280]" />
          <Kpi label="Kâr" value={fmtTL(profit)}
               color={profit >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"} testid="ec-profit" />
        </div>
      </Section>

      <Section title="Not (Opsiyonel)">
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  rows={2} placeholder="İç not — müşteriye görünmez" />
      </Section>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/ecommerce/sales")}>
          Vazgeç
        </Button>
        <Button type="submit" disabled={saving} data-testid="ec-save"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Satışı Kaydet"}
        </Button>
      </div>
    </form>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">{label}</div>
      <div className={`text-lg font-extrabold ${color}`} data-testid={testid}>{value}</div>
    </div>
  );
}
